import { BenefitingPerson, CountryEnum, LegalPerson, NaturalPerson } from 'api';
import React, { FunctionComponent } from 'react';
import Translate from 'ui/atoms/translate';
import BenefitingPersonsModal from 'apps/issuer/shared/kyc-and-compliance-view/add-manual-review/add-manual-review-modals/benefiting-persons-modal';
import AddIdentificationModal from './add-identification-modal';
import EditBankAccountModal from './edit-bank-account-modal';
import EditCompanyDataModal from './edit-company-data-modal';
import EditDepositAccountModal from './edit-deposit-account-modal';
import EditRegistrationAddressModal from './edit-registration-address-modal';
import EditTaxInformationModal from './edit-tax-information-modal';
import SuccessMessageModal from './success-message-modal';
import EditPersonalDataModal from './edit-personal-data-modal';

export interface IssuerInvestorEditModalsProps {
  id: string;
  isEditRegistrationAddressOpen: boolean;
  isEditCompanyDataOpen: boolean;
  isEditBankAccountOpen: boolean;
  isEditDepositAccountOpen: boolean;
  isEditTaxInformationOpen: boolean;
  isAddIdentificationOpen: boolean;
  isEditBenefitingPersonsOpen: boolean;
  isEditPersonalDataOpen: boolean;
  isSuccessEditOpen: boolean;
  setEditRegistrationAddressModal: (val: boolean) => void;
  setEditCompanyDataModal: (val: boolean) => void;
  setEditBankAccountModal: (val: boolean) => void;
  setEditDepositAccountModal: (val: boolean) => void;
  setEditTaxInformationModal: (val: boolean) => void;
  setAddIdentificationModal: (val: boolean) => void;
  setEditBenefitingPersonsModal: (val: boolean) => void;
  setEditPersonalDataOpen: (val: boolean) => void;
  fetchInvestorDetails: () => void;
  setSuccessModal: (val: boolean) => void;
  naturalPerson: NaturalPerson;
  legalPerson: LegalPerson;
  rootPerson: NaturalPerson | LegalPerson;
  benefitingOwners?: BenefitingPerson[];
  target2Bic?: string | null;
}

const IssuerInvestorEditModals: FunctionComponent<IssuerInvestorEditModalsProps> = (props) => {
  const {
    isEditRegistrationAddressOpen,
    isEditCompanyDataOpen,
    isEditBankAccountOpen,
    isEditDepositAccountOpen,
    isEditTaxInformationOpen,
    isAddIdentificationOpen,
    isEditBenefitingPersonsOpen,
    isEditPersonalDataOpen,
    isSuccessEditOpen,
    setEditRegistrationAddressModal,
    setEditCompanyDataModal,
    setEditBankAccountModal,
    setEditDepositAccountModal,
    setEditTaxInformationModal,
    setSuccessModal,
    setEditBenefitingPersonsModal,
    setAddIdentificationModal,
    setEditPersonalDataOpen,
    fetchInvestorDetails,
    naturalPerson,
    legalPerson,
    rootPerson,
    id,
    benefitingOwners,
    target2Bic,
  } = props;

  return (
    <>
      {isEditPersonalDataOpen && (
        <EditPersonalDataModal
          onHideEditPersonalData={() => setEditPersonalDataOpen(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          values={{
            id: id,
            forename: naturalPerson.forename,
            surname: naturalPerson.surname,
            birthDate: naturalPerson.birthDate,
            birthPlace: naturalPerson.birthPlace,
            nationality: naturalPerson.nationality,
            isSubjectToUsTax: (naturalPerson.isSubjectToUsTax || false).toString(),
          }}
        />
      )}
      {isEditRegistrationAddressOpen && (
        <EditRegistrationAddressModal
          onHideEditRegisterAddress={() => setEditRegistrationAddressModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          values={{
            id: id,
            street: naturalPerson.street || '',
            zip: naturalPerson.zip || '',
            city: naturalPerson.city || '',
            country: naturalPerson.country || CountryEnum.DEU,
          }}
        />
      )}
      {isEditCompanyDataOpen && (
        <EditCompanyDataModal
          onHideEditCompanyData={() => setEditCompanyDataModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          values={{
            id: id,
            companyName: legalPerson.companyName || '',
            legalEntityType: legalPerson.legalEntityType,
            street: legalPerson.street || '',
            zip: legalPerson.zip || '',
            city: legalPerson.city || '',
            country: legalPerson.country || CountryEnum.DEU,
            commercialRegister: legalPerson.commercialRegister || '',
            commercialRegisterNumber: legalPerson.commercialRegisterNumber || '',
            commercialRegisterStatement: legalPerson.commercialRegisterStatement,
            transparencyRegister: legalPerson.transparencyRegister,
            listOfShareholders: legalPerson.listOfShareholders,
            commercialRegisterStatementId: legalPerson.commercialRegisterStatement?.id,
            listOfShareholdersId: legalPerson.listOfShareholders?.id,
            transparencyRegisterId: legalPerson.transparencyRegister?.id,
          }}
        />
      )}
      {isAddIdentificationOpen && (
        <AddIdentificationModal
          onHideAddIdentification={() => setAddIdentificationModal(false)}
          showSuccessModal={() => setSuccessModal(true)}
          fetchInvestorDetails={fetchInvestorDetails}
          investorId={id}
          registeredAddress={{
            street: naturalPerson?.street || '',
            zip: naturalPerson?.zip || '',
            city: naturalPerson?.city || '',
            country: naturalPerson?.country || '',
          }}
          personalData={{
            forename: naturalPerson?.forename,
            surname: naturalPerson?.surname,
            dateOfBirth: naturalPerson?.birthDate || undefined,
            placeOfBirth: naturalPerson?.birthPlace || '',
            nationality: naturalPerson?.nationality || CountryEnum.DEU,
          }}
        />
      )}
      {isEditBankAccountOpen && (
        <EditBankAccountModal
          onHideEditBankAccount={() => setEditBankAccountModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          values={{
            id: id,
            accountHolder: rootPerson?.bankAccount?.accountHolder || '',
            iban: rootPerson.bankAccount?.iban || '',
            target2Bic: target2Bic || '',
          }}
        />
      )}
      {isEditDepositAccountOpen && (
        <EditDepositAccountModal
          onHideEditDepositAccount={() => setEditDepositAccountModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          values={{
            id: id,
            accountHolder: rootPerson.securitiesDepositAccount?.accountHolder || '',
            accountNumber: rootPerson.securitiesDepositAccount?.accountNumber || '',
            bic: rootPerson.securitiesDepositAccount?.bic || '',
          }}
        />
      )}
      {isEditTaxInformationOpen && (
        <EditTaxInformationModal
          onHideEditTaxInformation={() => setEditTaxInformationModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          showSuccessModal={() => setSuccessModal(true)}
          exemptionOrderSum={rootPerson.taxInformation?.exemptionOrderSum}
          values={{
            id: id,
            isSubjectToGermanTax: rootPerson?.taxInformation?.isSubjectToGermanTax,
            taxIdentificationNumber: rootPerson.taxInformation?.taxIdentificationNumber || '',
            nonAssessmentCertificate: rootPerson.taxInformation?.nonAssessmentCertificate || false,
            nonAssessmentCertificateDoc: rootPerson.taxInformation?.nonAssessmentCertificateDoc,
          }}
        />
      )}
      {isEditBenefitingPersonsOpen && (
        <BenefitingPersonsModal
          onHide={() => setEditBenefitingPersonsModal(false)}
          fetchInvestorDetails={fetchInvestorDetails}
          benefitingOwners={benefitingOwners}
          investorId={id}
        />
      )}
      {isSuccessEditOpen && (
        <SuccessMessageModal
          title={<Translate name="dashboardIssuerInvestorDetails.editSuccessModal.title" />}
          onHideSuccessEdit={() => setSuccessModal(false)}
        >
          <Translate name="dashboardIssuerInvestorDetails.editSuccessModal.message" />
        </SuccessMessageModal>
      )}
    </>
  );
};

export default IssuerInvestorEditModals;
